'use client';


/** Imports */
import 'dayjs/locale/en';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import {useLocale} from 'next-intl';
import {ThemeContext} from '@/context/ThemeContext';
import {type ReactNode, useContext, useMemo} from 'react';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import {deDE, enUS, esES, frFR, itIT} from '@mui/x-date-pickers-pro/locales';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';


/** Types */
type Props = Readonly<{
    children: ReactNode;
}>;


/** Component */
export function MuiProvider({children}: Props) {

    // Hooks
    const locale = useLocale();
    const {theme} = useContext(ThemeContext);

    // Memos
    const muiTheme = useMemo(() => {

        // Get localization
        let localization = enUS;
        if (locale === 'de') localization = deDE;
        else if (locale === 'it') localization = itIT;
        else if (locale === 'es') localization = esES;
        else if (locale === 'fr') localization = frFR;

        // Generate theme
        const colorLight = '#45337d';
        const colorDark = '#b19dff';
        const colorLightDarker = '#33265c';
        const colorDarkDarker = '#7f6ad3';
        const themeColor = (theme === 'light')
            ? colorLight
            : colorDark;
        const themeColorDarker = (theme === 'light')
            ? colorLightDarker
            : colorDarkDarker;
        const themeColorText = '#fff';
        return createTheme(
            {
                cssVariables: true,
                palette: {
                    mode: theme || 'light',
                    primary: {
                        main: themeColor,
                        contrastText: themeColorText,
                        dark: themeColorDarker,
                        mainChannel: themeColor,
                    } as any,
                    secondary: {
                        main: themeColor,
                        contrastText: themeColorText,
                        dark: themeColorDarker,
                    },
                },
            },
            localization,
        );
    }, [locale, theme]);


    // Render
    return (
        <ThemeProvider theme={muiTheme}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
                {children}
            </LocalizationProvider>
        </ThemeProvider>
    );
}
